import {Injectable} from '@angular/core';
import {TranslationItem} from "@core/shared/models/translation";
import {UserService} from "@core/shared/services/user.service";
import {User} from "@core/shared/models/user";
import {BehaviorSubject, Observable, Subject} from "rxjs";
import {HttpClient} from "@angular/common/http";
import {TranslateService} from "@ngx-translate/core";
import {allowedLocales} from '@core/shared/utils/locale';

@Injectable({
    providedIn: 'root'
})
export class TranslationService {

    public onLangChange: Subject<string> = new Subject<string>();

    private _translations: { [p: string]: object } = {};

    public translationsLoaded$: BehaviorSubject<boolean> = new BehaviorSubject(false);

    private _fallbacks: { [p: string]: string[] } = {
        'fr': ['fr', 'en', 'es', 'it', 'de', 'nl', 'pt'],
        'en': ['en', 'fr', 'es', 'it', 'de', 'nl', 'pt'],
        'es': ['es', 'en', 'fr', 'it', 'pt', 'de', 'nl'],
        'it': ['it', 'en', 'fr', 'es', 'de', 'nl', 'pt'],
        'de': ['de', 'nl', 'en', 'fr', 'es', 'it', 'pt'],
        'pt': ['pt', 'en', 'es', 'fr', 'it', 'de', 'nl'],
        'nl': ['nl', 'de', 'en', 'fr', 'es', 'it', 'pt']
    }

    constructor(
        private _httpClient: HttpClient,
        private _translateService: TranslateService,
        private _userService: UserService
    ) {}

    public getFallbackTranslation<T extends TranslationItem>(translations: Array<T>, user?: User, checkedLocale?: string): T|null {

        if(!translations.length){

            return null;
        }

        const checkedUser: User = user || this._userService.currentUser.getValue();

        if(!checkedUser){

            return translations[0];
        }

        const fallbackLocales: string[] = this._fallbacks[checkedLocale ? checkedLocale : checkedUser.locale];

        const existingLocales = translations.map((translation: T): string => {

            return translation.locale;
        });

        const locale = fallbackLocales.reduce((previousValue, currentValue): string => {

            return (!previousValue && existingLocales.includes(currentValue)) ? currentValue : previousValue;

        }, null);

        return translations.find((translation: T): boolean => {

            return translation.locale === locale;

        }) || translations[0];
    }

    public getUserLocale(): string {

        return this._userService.currentUser.getValue()?.locale;
    }

    public getTranslationFile$(locale: string): Observable<object>{

        return this._httpClient.get(`assets/i18n/${locale}.json`);
    }

    public getTranslationForLocale(locale: string, key: string): string  {

        const source: object = (locale in this.translations) ? this.translations[locale] : null;

        return source ? source[key] : null;
    }

    public getTranslationForLocaleWithFallback(locale: string, key: string): string  {

        const fallbackLocale: string = this.getFallbackLocale(locale);

        const source: object = (fallbackLocale in this.translations) ? this.translations[fallbackLocale] : null;

        return source ? source[key] : null;
    }

    public getFallbackLocale(locale: string): string {

        const fallbackLocales: string[] = this._fallbacks[locale];

        return fallbackLocales.reduce((previousValue, currentValue): string => {

            return (!previousValue && allowedLocales.includes(currentValue)) ? currentValue : previousValue;

        }, null);
    }

    public getTranslationFromQuantity(quantity: number, emptyTranslation: string, singularTranslation: string, pluralTranslation: string): string {

        switch (quantity) {

            case 0:

                return this._translateService.instant(emptyTranslation);

            case 1:

                return this._translateService.instant(singularTranslation);

            default:

                return this._translateService.instant(pluralTranslation, { quantity: quantity });
        }
    }

    set translations(translations: { [p: string]: object }) {

        this._translations = translations;

        this.translationsLoaded$.next(true);
    }

    get translations(): { [p: string]: object }{

        return this._translations;
    }
}
